import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { KeyboardDatePicker } from '@material-ui/pickers'
import CircularProgress from '@material-ui/core/CircularProgress'
import {
  useIfapaStats,
  usePostIfapaStats,
} from '@graniot/common/adapters/ifapastats'
import { useHistoricalWeatherData } from '@graniot/common/adapters/historical-weather-data'
import { useTranslation } from 'react-i18next'
import {
  format,
  parse,
  eachDayOfInterval,
  subDays,
} from 'date-fns'
import { InputForm } from './InputForm'
import { CloudCoverageControl } from './CloudCoverageControl'
import { LayerSelector } from '@graniot/common/components/map/dialog/LayerSelector'

import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

import { Responsive, WidthProvider } from 'react-grid-layout'

import {
  Grid,
  Tooltip,
  IconButton,
  Card,
  CardHeader,
  CardContent,
  Menu,
  MenuItem,
  Typography,
  Divider,
} from '@material-ui/core'
import HelpIcon from '@material-ui/icons/Help'
import Alert from '@material-ui/lab/Alert'
import { MoreVert as MoreVertIcon } from '@material-ui/icons'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { BarChart, LineChart, LinesChart } from 'echarts/charts'
import {
  GridComponent,
  ToolboxComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  TransformComponent,
  DatasetComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { groupBy } from 'lodash'
import { useAuth } from '../../../../context/AuthContext'
import { unparse } from 'papaparse';

// -- ECharts setup
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  BarChart,
  CanvasRenderer,
  LinesChart,
  DatasetComponent,
  ToolboxComponent,
  LegendComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  TransformComponent,
])

// -- Brand chart palette (aligned to docs/design-system.md)
const CHART_COLORS = {
  index: '#37A692', // accent teal — satellite index (FIS) line
  rain: '#004c96',  // rain bars (blue)
}

// -- Styles
const useStyles = makeStyles((theme) => ({
  margin: {
    margin: theme.spacing(1),
  },
  filterPanel: {
    position: '-webkit-sticky',
    position: 'sticky',
    top: 0,
    zIndex: 5,
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  filterGroup: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  groupCaption: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  periodRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& > .MuiTextField-root': {
      flex: 1,
      minWidth: 0,
    },
  },
  rangeArrow: {
    color: theme.palette.text.secondary,
    fontSize: '1.1rem',
    lineHeight: 1,
  },
  groupDivider: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(1),
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
  },
  dragHandle: {
    cursor: 'move',
  },
  title: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
  },
}))

// -- Layout config
const ResponsiveGridLayout = WidthProvider(Responsive)
const layouts = {
  lg: [
    { w: 12, h: 4, x: 0, y: 0,  i: 'graph1' },
    { w: 12, h: 4, x: 0, y: 4,  i: 'graph2' },
    { w: 12, h: 4, x: 0, y: 8,  i: 'graph3' },
    { w: 12, h: 4, x: 0, y: 12, i: 'graph4' },
    { w: 12, h: 4, x: 0, y: 16, i: 'graph5' },
    { w: 12, h: 4, x: 0, y: 20, i: 'graph6' },
  ],
  md: [
    { w: 12, h: 4, x: 0, y: 0,  i: 'graph1' },
    { w: 12, h: 4, x: 0, y: 4,  i: 'graph2' },
    { w: 12, h: 4, x: 0, y: 8,  i: 'graph3' },
    { w: 12, h: 4, x: 0, y: 12, i: 'graph4' },
    { w: 12, h: 4, x: 0, y: 16, i: 'graph5' },
    { w: 12, h: 4, x: 0, y: 20, i: 'graph6' },
  ],
  sm: [
    { w: 12, h: 4, x: 0, y: 0,  i: 'graph1' },
    { w: 12, h: 4, x: 0, y: 4,  i: 'graph2' },
    { w: 12, h: 4, x: 0, y: 8,  i: 'graph3' },
    { w: 12, h: 4, x: 0, y: 12, i: 'graph4' },
    { w: 12, h: 4, x: 0, y: 16, i: 'graph5' },
    { w: 12, h: 4, x: 0, y: 20, i: 'graph6' },
  ],
  xs: [
    { w: 12, h: 4, x: 0, y: 0,  i: 'graph1' },
    { w: 12, h: 4, x: 0, y: 4,  i: 'graph2' },
    { w: 12, h: 4, x: 0, y: 8,  i: 'graph3' },
    { w: 12, h: 4, x: 0, y: 12, i: 'graph4' },
    { w: 12, h: 4, x: 0, y: 16, i: 'graph5' },
    { w: 12, h: 4, x: 0, y: 20, i: 'graph6' },
  ],
}

// ----------------------------------------------------------------------------
// Helper: list every date from "fromDate" to "toDate"
// ----------------------------------------------------------------------------
function getEachDayOfRange(fromDate, toDate) {
  const start = parse(fromDate, 'yyyy-MM-dd', new Date())
  const end   = parse(toDate,   'yyyy-MM-dd', new Date())
  if (isNaN(start) || isNaN(end) || start > end) {
    return []
  }
  const days = eachDayOfInterval({ start, end })
  return days.map((d) => format(d, 'yyyy-MM-dd'))
}

// ----------------------------------------------------------------------------
// Main component
// ----------------------------------------------------------------------------
const AdvancedData = ({
  parcel,
  layers,
  selectedResolution,
  maxCC,
  setMaxCC,
  isLoadingParcelFISHistory,
  dataParcelFISHistory,
  refetchParcelFISHistory,
  selectedLayer,
  handleSelectedLayer,
}) => {
  const classes = useStyles()
  const { loggedUser } = useAuth()
  const { t, i18n } = useTranslation()

  // -- Dates
  const [fromDate, setFromDate] = React.useState(
    format(subDays(new Date(), 37), 'yyyy-MM-dd')
  )
  const [toDate, setToDate] = React.useState(format(new Date(), 'yyyy-MM-dd'))

  // -- Data used by the charts
  const [chartDataR, setChartDataR] = React.useState({})
  const [chartDataMean, setChartDataMean] = React.useState({})
  const [chartDataI, setChartDataI] = React.useState({})

  // -- Ifapa thresholds
  const [uInf, setUInf] = React.useState(6)
  const [uSup, setUSup] = React.useState(30)
  const [formattedData, setFormattedData] = React.useState([]);

  // -- Menu anchor
  const [anchorEl, setAnchorEl] = React.useState(null)

  // -- Adapters
  const {
    data: dataR,
    refetch: refetchR,
    isLoading: isLoadingR,
  } = useHistoricalWeatherData(parcel.id, fromDate, toDate, i18n.language)

  const {
    data: dataI,
    refetch: refetchI,
    isLoading: isLoadingI,
  } = useIfapaStats(parcel.id, fromDate, toDate)

  const { data: dataP, refetch: refetchP } = usePostIfapaStats({
    id: parcel.id,
    from: fromDate,
    to: toDate,
    uInf,
    uSup,
  })

  // ----------------------------------------------------------------------------
  // Weather-data status flags
  // The backend may return HTTP 200 with { status: 'error', info: ... }
  // (e.g. a server-side failure). In that case dataR is truthy but has no
  // `.data` array, so charts must guard on `hasWeatherData` (not just `dataR`)
  // and the error must be surfaced to the user.
  // ----------------------------------------------------------------------------
  const weatherError = dataR?.status === 'error'
  const hasWeatherData = Array.isArray(dataR?.data)

  // IFAPA source caption (only when data comes from an IFAPA station).
  // Rendered as the CardHeader subheader on each chart.
  const ifapaSubheader =
    dataR?.source === 'ifapa' && dataR?.station ? (
      <Typography variant="caption" color="textSecondary">
        {t('IfapaData.SourceLabel', {
          name: dataR.station.name,
          distance: Number(dataR.station.distance_km).toFixed(1),
        })}
      </Typography>
    ) : null

  // ----------------------------------------------------------------------------
  // Convert the user-chosen date to 'yyyy-MM-dd'
  // ----------------------------------------------------------------------------
  const handleDateChange = (value) => {
    try {
      return format(value, 'yyyy-MM-dd')
    } catch (err) {
      return value
    }
  }

  // ----------------------------------------------------------------------------
  // Whenever fromDate or toDate changes -> refetch all needed data
  // ----------------------------------------------------------------------------
  React.useEffect(() => {
    if (fromDate && toDate) {
      refetchR()
    }
  }, [fromDate, toDate, parcel, refetchR, refetchI])

  // ----------------------------------------------------------------------------
  // Re-fetch FIS History when the date or layer changes
  // ----------------------------------------------------------------------------
  React.useEffect(() => {
    refetchParcelFISHistory()
  }, [selectedLayer, fromDate, toDate, maxCC, refetchParcelFISHistory])

  // ----------------------------------------------------------------------------
  // Once dataR arrives -> group by date -> store in chartDataR
  // ----------------------------------------------------------------------------
  React.useEffect(() => {
    if (Array.isArray(dataR?.data)) {
      setChartDataR(
        groupBy(dataR.data, (d) =>
          format(parse(d.date, 'yyyy-LL-dd', new Date()), 'yyyy-MM-dd')
        )
      )
    }
  }, [dataR])

  // ----------------------------------------------------------------------------
  // Once dataParcelFISHistory arrives -> group by date -> chartDataMean
  // ----------------------------------------------------------------------------
  React.useEffect(() => {
    if (Array.isArray(dataParcelFISHistory)) {
      const filtered = dataParcelFISHistory.filter((f) => f.date >= fromDate)
      setChartDataMean(
        groupBy(filtered, (d) =>
          format(parse(d.date, 'yyyy-LL-dd', new Date()), 'yyyy-MM-dd')
        )
      )
    }
  }, [dataParcelFISHistory, fromDate])



  React.useEffect(() => {
    if (dataR?.data) {
      const updatedData = formatDataI();
      setFormattedData(updatedData);
    }
  }, [uInf, uSup, dataR]);


  // Handlers for the menu
  const handleClick = (e) => setAnchorEl(e.currentTarget)
  const handleCloseMenu = () => setAnchorEl(null)

  // Handlers for the Ifapa thresholds
  const handleUInfChange = (e) => setUInf(e.target.value)
  const handleUSupChange = (e) => setUSup(e.target.value)

  const handleUInfSubmit = (e) => {
    e.preventDefault()
    refetchP()
  }
  const handleUSupSubmit = (e) => {
    e.preventDefault()
    refetchP()
  }

  const downloadCSV = () => {
    if (!dataR?.data) return;
  
    // Check if 'Año' or 'Year' exists in metadata
    const year = parcel.properties.metadata?.Año || parcel.properties.metadata?.Year;
  
    // Prepare data for export dynamically
    const out = dataR.data.map((d) => {
      const row = {
        parcel_id: parcel.id,
        parcel_name: parcel.properties.name,
        date: d.date,
        precipitation_sum: d.rain_sum,
        precipitation_acc: d.rain_acc,
      };
  
      // Add the 'year' column only if it exists
      if (year) {
        row.parcel_year = year;
      }
  
      return row;
    });
  
    // Convert to CSV using PapaParse
    const csvData = unparse(out);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const sanitizedFileName = `precipitation_${parcel.id}_${parcel.properties.name}`
      .replace(/[^a-zA-Z0-9-_]/g, '_'); // Sanitize invalid filename characters
  
    const link = document.createElement('a');
    link.href = url;
    link.download = `${sanitizedFileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ----------------------------------------------------------------------------
  // Format data for Daily Rain (rain_sum) 
  // ----------------------------------------------------------------------------

const formatDataRainfallDaily = () => {
  const chartHeader = [['day', 'year', 'rainfall']];
  const rows = [];

  getEachDayOfRange(fromDate, toDate).forEach((dayStr) => {
    const records = chartDataR[dayStr];

    if (!records || !records[0]) {
      // No data => push 0
      rows.push([
        format(parse(dayStr, 'yyyy-MM-dd', new Date()), 'yyyy-MM-dd'),
        format(parse(dayStr, 'yyyy-MM-dd', new Date()), 'yyyy'),
        null,
      ]);
      return;
    }

    const item = records[0];
    const dateObj = parse(item.date, 'yyyy-MM-dd', new Date());
    const yearStr = format(dateObj, 'yyyy');
    const rainVal = item?.rain_sum ?? 0;

    rows.push([
      format(dateObj, 'yyyy-MM-dd'),
      format(dateObj, 'yyyy'),
      parseFloat(rainVal),
    ]);
  });

  return chartHeader.concat(rows);
};

  // ----------------------------------------------------------------------------
  // Format the FIS Mean (chartDataMean)
  // ----------------------------------------------------------------------------
  const formatDataRainMean = () => {
    const chartHeader = [['day', 'year', 'mean']]
    const rows = []

    getEachDayOfRange(fromDate, toDate).forEach((dayStr) => {
      const dayRecords = chartDataMean[dayStr]
      if (!dayRecords || !dayRecords[0]) {
        rows.push([
          format(parse(dayStr, 'yyyy-MM-dd', new Date()), 'yyyy-MM-dd'),
          format(parse(dayStr, 'yyyy-MM-dd', new Date()), 'yyyy'),
          null,
        ])
        return
      }
      const item = dayRecords[0]
      const dateObj = parse(item.date, 'yyyy-LL-dd', new Date())
      const meanVal = item.basicStats?.mean

      rows.push([
        format(new Date(dateObj), 'yyyy-MM-dd'),
        format(dateObj, 'yyyy'),
        meanVal != null ? parseFloat(meanVal) : null,
      ])
    })
    return chartHeader.concat(rows)
  }

  // ----------------------------------------------------------------------------
  // Format data for Accumulated Rain (rain_acc)
  // ----------------------------------------------------------------------------
  const formatDataRainAcc = () => {
  const chartHeader = [['day', 'year', 'rainAcc']];
  const rows = [];

  dataR?.data?.forEach((item) => {
    const dateObj = parse(item.date, 'yyyy-MM-dd', new Date());
    rows.push([
      format(dateObj, 'yyyy-MM-dd'),
      format(dateObj, 'yyyy'),
      item?.rain_acc ? parseFloat(item.rain_acc) : null,
    ]);
  });

  return chartHeader.concat(rows);
};

  // ----------------------------------------------------------------------------
  // Format Ifapa data (unchanged logic)
  // ----------------------------------------------------------------------------
  //
const formatDataI = () => {
  const chartHeader = [
    ['day', 'year', 'mean', 'max', 'min', 'et0', 'hum', 'acc', 'thermal_integral'],
  ]
  const rows = []
  let cumulativeIntegral = 0;
  dataR?.data?.forEach((item) => {
    const dateObj = parse(item.date, 'yyyy-MM-dd', new Date())

    const meanTemp = item?.temperature_2m_mean ? parseFloat(item.temperature_2m_mean) : null;

    // Calculate daily contribution to the thermal integral
    let dailyIntegral = 0;
    if (meanTemp !== null && meanTemp > uInf && meanTemp < uSup) {
      dailyIntegral = meanTemp - uInf; // Difference from lower threshold
      cumulativeIntegral += dailyIntegral; // Accumulate daily contributions
    }

    rows.push([
      format(new Date(dateObj), 'yyyy-MM-dd'),
      format(dateObj, 'yyyy'),
      item?.temperature_2m_mean   ? parseFloat(item.temperature_2m_mean) : null,
      item?.temperature_2m_max    ? parseFloat(item.temperature_2m_max)  : null,
      item?.temperature_2m_min    ? parseFloat(item.temperature_2m_min)  : null,
      item?.et0_fao_evapotranspiration ? parseFloat(item.et0_fao_evapotranspiration) : null,
      item?.relative_humidity_2m  ? parseFloat(item.relative_humidity_2m) : null,
      item?.rain_acc              ? parseFloat(item.rain_acc) : null,
      parseFloat(cumulativeIntegral.toFixed(2)),
    ])
  })
  return chartHeader.concat(rows)


}
  // ----------------------------------------------------------------------------
  // structDataset for the first chart: Daily Rain + FIS Mean
  // ----------------------------------------------------------------------------
  const structDataset = (dat) => {
    if (dat === 'mean') {
      // Bind series directly to the source datasets. (Previously these went
      // through no-op `filter` transforms — `year != null` keeps every row —
      // and ECharts doesn't re-run dataset transforms on a merge update, so the
      // FIS line painted stale data when the cloud-coverage filter changed.)
      return [
        {
          id: 'stats_dataset_rainDaily',
          source: formatDataRainfallDaily(),
        },
        {
          id: 'stats_dataset_fisMean',
          source: formatDataRainMean(),
        },
      ]
    } else if (dat === 'rainAcc') {
      // For the second chart
      const dataset = [
        {
          id: 'rainAcc_dataset',
          source: formatDataRainAcc(),
        },
      ]
      const filterRainAcc = {
        id: 'dataset_of_rainAcc',
        fromDatasetId: 'rainAcc_dataset',
        transform: {
          type: 'filter',
          config: {
            or: [{ dimension: 'year', '!=': null }],
          },
        },
      }
      return [...dataset, filterRainAcc]
    }
    return null
  }

  // structDataset for Ifapa
  const structDatasetI = () => {
    const ds = [
      {
        id: 'stats_dataset',
        source: hasWeatherData ? formatDataI() : [],
      },
    ]
    const filterIfapa = {
      id: 'dataset_of_ifapa',
      fromDatasetId: 'stats_dataset',
      transform: {
        type: 'filter',
        config: {
          or: [{ dimension: 'year', '!=': null }],
        },
      },
    }
    return [...ds, filterIfapa]
  }

  // ----------------------------------------------------------------------------
  // structSeries for the first chart
  // ----------------------------------------------------------------------------
  const structSeries = (dat) => {
    if (dat === 'mean') {
      return [
        // Bar for daily rainfall
        {
          type: 'bar',
          name: t('Precipitation.DailyPrec'),
          datasetId: 'stats_dataset_rainDaily',
          yAxisIndex: 0,
          encode: {
            x: 'day',
            y: 'rainfall',
          },
          itemStyle: { color: CHART_COLORS.rain },
        },
        // Line for FIS Mean
        {
          type: 'line',
          name: `${selectedLayer?.name} ${t('mean')}`,
          datasetId: 'stats_dataset_fisMean',
          yAxisIndex: 1,
          connectNulls: true,
          smooth: true,
          color: CHART_COLORS.index,
          encode: {
            x: 'day',
            y: 'mean',
          },
        },
      ]
    }
    return null
  }

  // Series for Accumulated rainfall
  const structSeriesRainAcc = () => ({
    type: 'line',
    name: t('Grafico.Titulo_Punto_Grafica_Mes'),
    datasetId: 'dataset_of_rainAcc',
    fromDatasetId: 'rainAcc_dataset',
    yAxisIndex: 0,
    connectNulls: true,
    smooth: true,
    color: 'blue',
    encode: {
      x: 'day',
      y: 'rainAcc',
    },
  })

  // Ifapa stats series
  const structSeriesI = (dat) => {
    
    const isLine = dat === 'et0' || dat === 'acc'
    return {
      type: isLine ? 'line' : 'bar',
      name: dat,
      datasetId: 'dataset_of_ifapa',
      fromDatasetId: 'stats_dataset',
      connectNulls: true,
      smooth: true,
      areaStyle: isLine ? { color: 'blue' } : undefined,
      encode: {
        x: 'day',
        y: dat,
      },
    }
  }

  // ----------------------------------------------------------------------------
  // Rendering
  // ----------------------------------------------------------------------------
  return (
    <div>
      {/* Filter panel: Period · Index layer · Max cloud coverage */}
      <div className={classes.filterPanel}>
        <div className={classes.filterRow}>

          {/* PERIOD */}
          <div className={classes.filterGroup}>
            <span className={classes.groupCaption}>{t('Period')}</span>
            <div className={classes.periodRow}>
              <KeyboardDatePicker
                name="from_date"
                aria-label={t('date_start')}
                value={fromDate}
                onChange={(date) => setFromDate(handleDateChange(date))}
                format="dd/MM/yyyy"
                minDate={new Date('2020-01-01')}
                maxDate={new Date()}
                autoOk
                inputVariant="standard"
                fullWidth
              />
              <span className={classes.rangeArrow}>→</span>
              <KeyboardDatePicker
                name="to_date"
                aria-label={t('date_end')}
                value={toDate}
                onChange={(date) => setToDate(handleDateChange(date))}
                format="dd/MM/yyyy"
                minDate={fromDate}
                maxDate={new Date()}
                autoOk
                inputVariant="standard"
                fullWidth
              />
            </div>
          </div>

          <Divider orientation="vertical" flexItem className={classes.groupDivider} />

          {/* INDEX LAYER */}
          <div className={classes.filterGroup}>
            <span className={classes.groupCaption}>{t('Layers')}</span>
            <LayerSelector
              layers={layers.filter(layer =>
                !layer.name.endsWith('WMS') &&
                !layer.name.startsWith('NATURA_COLOR') &&
                !layer.name.startsWith('NATURAL_COLOUR') &&
                !layer.name.startsWith('NATURA-COLOR') &&
                !layer.name.startsWith('NATURAL-COLOUR')
              )}
              selectedLayer={selectedLayer}
              handleSelectedLayer={handleSelectedLayer}
              hideLabel
              width="100%"
            />
          </div>

          {/* MAX CLOUD COVERAGE (satellite resolutions only) */}
          {['10x10 meters', '3x3 meters'].includes(selectedResolution?.resolution) && (
            <>
              <Divider orientation="vertical" flexItem className={classes.groupDivider} />
              <div className={classes.filterGroup}>
                <CloudCoverageControl
                  valueFraction={maxCC}
                  onCommitFraction={(fraction) => setMaxCC(fraction)}
                  info
                />
              </div>
            </>
          )}

        </div>
      </div>

      {weatherError && (
        <Alert severity="error" className={classes.margin}>
          {t('Weather data could not be loaded. Please try again later.')}
          {loggedUser?.is_superuser && dataR?.info && (
            <div style={{ fontSize: '0.85em', opacity: 0.8, marginTop: 4 }}>
              {dataR.info}
            </div>
          )}
        </Alert>
      )}

      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        isResizable
        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
        draggableHandle=".drag-handle"
      >
        {/* ---------------------------------------------------------------
          Graph 1: Daily Rain (blue bar) + FIS Mean (green line)
        --------------------------------------------------------------- */}
        <div key="graph1">
          <Card elevation={0} className={classes.card}>
            <CardHeader
              classes={{ title: classes.title, root: `drag-handle ${classes.dragHandle}` }}
              title={t('Precipitation.DailyPrec')}
              subheader={ifapaSubheader}
              titleTypographyProps={{ variant: 'h6' }}
              action={
                dataR &&
                loggedUser?.is_superuser && (
                  <>
                    <IconButton
                      aria-label="more"
                      onClick={handleClick}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      keepMounted
                      open={Boolean(anchorEl)}
                      onClose={handleCloseMenu}
                    >
                      <MenuItem onClick={downloadCSV}>
                        {t('Grafica.Boton_DownloadCSV')}
                      </MenuItem>
                    </Menu>
                  </>
                )
              }
            />
            <CardContent style={{ height: '100%' }}>
              <ReactEChartsCore
                echarts={echarts}
                style={{ height: '90%' }}
                notMerge
                showLoading={isLoadingR || isLoadingParcelFISHistory}
                option={{
                  legend: {},
                  dataset: hasWeatherData ? structDataset('mean') : [],
                  xAxis: {
                    type: 'category',
                  },
                  yAxis: [
                    {
                      name: t('Precipitation.DailyPrec'),
                      position: 'left',
                      type: 'value',
                    },
                    {
                      name: t('mean'),
                      position: 'right',
                      type: 'value',
                    },
                  ],
                  series: structSeries('mean'),
                  dataZoom: [
                    { type: 'inside' },
                    { start: 0, end: 20 },
                  ],
                  tooltip: {
                    trigger: 'axis',
                    formatter: (params) => {
                      if (!params || !params.length) return ''

                      let tooltipText = ''

                      const dateObj = params[0].data[0]
                      if (dateObj) {
                        tooltipText += dateObj + '<br/>'
                      }

                      params.forEach((item) => {
                        const rawVal = parseFloat(item.data[2])

                        if (!Number.isNaN(rawVal)) {
                          const val2dec = rawVal.toFixed(2)

                          let unit = ''
                          if (item.seriesName.toLowerCase().includes('rain')) {
                            unit = ' mm'
                          }

                          tooltipText += `${item.marker} ${item.seriesName}: <b>${val2dec}${unit}</b><br/>`
                        }
                      })

                      return tooltipText
                    },
                    axisPointer: {
                      type: 'cross',
                      animation: false,
                      label: { backgroundColor: '#505765' },
                    },
                  },
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* ---------------------------------------------------------------
          Graph 2: Accumulated Rain
        --------------------------------------------------------------- */}
        <div key="graph2">
          <Card elevation={0} className={classes.card}>
            <CardHeader
              classes={{ title: classes.title, root: `drag-handle ${classes.dragHandle}` }}
              title={t('Grafica.Titulo_Punto_Grafica_Mes')}
              subheader={ifapaSubheader}
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent style={{ height: '100%' }}>
              <ReactEChartsCore
                echarts={echarts}
                style={{ height: '90%' }}
                showLoading={isLoadingR}
                option={{
                legend: {},
                dataset: {
                    source: hasWeatherData ? formatDataRainAcc() : [],
                },
                xAxis: {
                  type: 'category',
                },
                yAxis: {
                  name: t('Precipitation.DailyPrec'),
                  type: 'value',
                },
                series: [
                  {
                    type: 'line',
                    name:  t('Grafica.Titulo_Punto_Grafica_Mes'),
                    connectNulls: true,
                    smooth: true,
                    color: 'blue',
                    encode: {
                      x: 'day',
                      y: 'rainAcc',
                    },
                  },
                ],
                dataZoom: [
                  { type: 'inside' },
                  { start: 0, end: 20 },
                ],
                tooltip: {
                  trigger: 'axis',
                  formatter: (params) => {
                    if (!params || !params.length) return ''

                    let tooltipText = ''

                    const dateObj = params[0].data[0]
                    if (dateObj) {
                      tooltipText += dateObj + '<br/>'
                    }

                    params.forEach((item) => {
                      const rawVal = parseFloat(item.data[2])

                      if (!Number.isNaN(rawVal)) {
                        const val2dec = rawVal.toFixed(2)

                        let unit = ''
                        if (item.seriesName.toLowerCase().includes('rain')) {
                          unit = ' mm'
                        }

                        tooltipText += `
                          ${item.marker} 
                          ${item.seriesName}: 
                          <b>${val2dec}${unit}</b><br/>
                        `
                      }
                    })

                    return tooltipText
                  },
                  axisPointer: {
                    type: 'cross',
                    animation: false,
                    label: { backgroundColor: '#505765' },
                  },
                },
              }}
              />
            </CardContent>
          </Card>
        </div>

        {/* ---------------------------------------------------------------
          Graph 3: Integral Térmica
        --------------------------------------------------------------- */}
        <div key="graph3">
          <Card elevation={0} className={classes.card}>
            <CardHeader
              classes={{ title: classes.title, root: `drag-handle ${classes.dragHandle}` }}
              title={t('Integral Tèrmica')}
              subheader={
                !dataR && !isLoadingR ? (
                  <Alert severity="warning">
                    {t('Data not available for this zone.')}
                  </Alert>
                ) : (
                  ifapaSubheader
                )
              }
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent style={{ height: '100%' }}>
              {dataR && !isLoadingR && (
                <Grid container spacing={0}>
                  <Grid item className={classes.margin}>
                    <InputForm
                      inputValue={uInf}
                      handleChange={handleUInfChange}
                      handleSubmit={handleUInfSubmit}
                      hText={t('IfapaData.FormUInf')}
                    />
                  </Grid>
                  <Grid item className={classes.margin}>
                    <InputForm
                      inputValue={uSup}
                      handleChange={handleUSupChange}
                      handleSubmit={handleUSupSubmit}
                      hText={t('IfapaData.FormUSup')}
                    />
                  </Grid>
                  <Grid item className={classes.margin}>
                    <Tooltip
                      title={<p style={{ fontSize: '1.2em', color: 'whitesmoke' }}>{t('IfapaData.FormInfo')}</p>}
                      placement="top"
                      arrow
                    >
                      <IconButton>
                        <HelpIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              )}
              <ReactEChartsCore
                echarts={echarts}
                style={{ height: '70%' }}
                showLoading={isLoadingR}
                option={{
                  color: '#004c96',
                  legend: {},
                  dataset: {
                    source: hasWeatherData ? formattedData : [],
                  },
                  xAxis: {
                    type: 'category',
                  },
                  yAxis: {
                    name: t('Integral Tèrmica'),
                    type: 'value',
                    axisLabel: { formatter: '{value} ºC' },
                  },
                 series: [
                   {
                     type: 'line',
                     name: t('Integral Tèrmica'),
                     connectNulls: true,
                     smooth: true,
                     color: '#FF5733',
                     encode: {
                       x: 'day',
                       y: 'thermal_integral',
                     },
                   },
                 ],
                  dataZoom: [
                    { type: 'inside' },
                    { start: 0, end: 20 },
                  ],

                  tooltip: {
                    trigger: 'axis',
                    formatter: (params) => {
                      if (!params?.length) return '';
                      const thermalIntegral = parseFloat(params[0].data[8])?.toFixed(2);
                      return `
                        ${params[0].name}<br />
                        ${params[0].marker} ${t('Integral Tèrmica')}: <b>${thermalIntegral ?? 'N/A'} ºC</b>
                      `;
                    },
                    axisPointer: {
                      type: 'cross',
                      animation: false,
                      label: { backgroundColor: '#505765' },
                    },
                  },
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* ---------------------------------------------------------------
          Graph 4: Daily Temperature (Min, Max, Mean)
        --------------------------------------------------------------- */}
        <div key="graph4">
          <Card elevation={0} className={classes.card}>
            <CardHeader
              classes={{ title: classes.title, root: `drag-handle ${classes.dragHandle}` }}
              title={t('IfapaData.DailyTemp')}
              subheader={
                !dataR && !isLoadingR ? (
                  <Alert severity="warning">
                    {t('Data not available for this zone.')}
                  </Alert>
                ) : (
                  ifapaSubheader
                )
              }
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent style={{ height: '100%' }}>
              <ReactEChartsCore
                echarts={echarts}
                style={{ height: '90%' }}
                showLoading={isLoadingR}
                option={{
                  color: '#004c96',
                  legend: {},
                  dataset: {
                    source: hasWeatherData ? formatDataI() : [],
                  },
                  xAxis: {
                    type: 'category',
                  },
                  yAxis: {
                    name: t('IfapaData.DailyTemp'),
                    type: 'value',
                    axisLabel: { formatter: '{value} ºC' },
                  },
                  series: [
                    {
                      type: 'line',
                      name: t('mean'),
                      connectNulls: true,
                      encode: { x: 'day', y: 'mean' },
                      itemStyle: { color: '#C9E798' },
                    },
                    {
                      type: 'line',
                      name: t('min'),
                      connectNulls: true,
                      encode: { x: 'day', y: 'min' },
                      itemStyle: { color: '#004c96' },
                    },
                    {
                      type: 'line',
                      name: t('max'),
                      connectNulls: true,
                      encode: { x: 'day', y: 'max' },
                      itemStyle: { color: '#FF3300' },
                    },
                  ],
                  dataZoom: [
                    { type: 'inside' },
                    { start: 0, end: 20 },
                  ],
                    tooltip: {
                      trigger: 'axis',
                      formatter: (params) => {
                        if (!params?.length) return '';
                        const day = params[0].value[0];
                        let tooltipContent = `${day}<br/>`;
                        const seriesData = {};
                        params.forEach(param => {
                          const seriesName = param.seriesName;
                          const value = param.value[param.encode.y[0]];
                          if (value !== undefined && value !== null) {
                            seriesData[seriesName] = {
                              value,
                              marker: param.marker
                            };
                          }
                        });
                        const orderedSeries = [t('max'), t('mean'), t('min')];
                        orderedSeries.forEach(seriesName => {
                          if (seriesData[seriesName]) {
                            tooltipContent += `${seriesData[seriesName].marker} ${seriesName} <b>${parseFloat(seriesData[seriesName].value).toFixed(2)} ºC</b><br/>`;
                          }
                        });
                        return tooltipContent;
                      },
                    axisPointer: {
                      type: 'cross',
                      animation: false,
                      label: { backgroundColor: '#505765' },
                    },
                  },
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* ---------------------------------------------------------------
          Graph 5: ET0
        --------------------------------------------------------------- */}
        <div key="graph5">
          <Card elevation={0} className={classes.card}>
            <CardHeader
              classes={{ title: classes.title, root: `drag-handle ${classes.dragHandle}` }}
              title={t('IfapaData.Et0')}
              subheader={
                !dataR && !isLoadingR ? (
                  <Alert severity="warning">
                    {t('Data not available for this zone.')}
                  </Alert>
                ) : (
                  ifapaSubheader
                )
              }
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent style={{ height: '100%' }}>
              <ReactEChartsCore
                echarts={echarts}
                style={{ height: '90%' }}
                showLoading={isLoadingR}
                option={{
                  color: '#004c96',
                  legend: {},
                  dataset: {
                    source: hasWeatherData ? formatDataI() : [],
                  },
                  xAxis: { type: 'category' },
                  yAxis: {
                    name: t('IfapaData.Et0'),
                    type: 'value',
                    axisLabel: { formatter: '{value} mm' },
                  },
                  series: [
                    {
                      type: 'bar',
                      name: 'et0',
                      connectNulls: true,
                      encode: { x: 'day', y: 'et0' },
                      itemStyle: { color: '#F04E41' },
                    },
                  ],
                  dataZoom: [
                    { type: 'inside' },
                    { start: 0, end: 20 },
                  ],
                  tooltip: {
                    trigger: 'axis',
                    formatter: (params) => {
                      if (!params?.length) return ''
                      const val = parseFloat(params[0].value[5])?.toFixed(2)
                      return `
                        ${params[0].value[0]}<br />
                        ${params[0].marker} Et0: <b>${val} mm</b>
                      `
                    },
                    axisPointer: {
                      type: 'cross',
                      animation: false,
                      label: { backgroundColor: '#505765' },
                    },
                  },
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* ---------------------------------------------------------------
          Graph 6: Humidity
        --------------------------------------------------------------- */}
        <div key="graph6">
          <Card elevation={0} className={classes.card}>
            <CardHeader
              classes={{ title: classes.title, root: `drag-handle ${classes.dragHandle}` }}
              title={t('IfapaData.Hum')}
              subheader={
                !dataR && !isLoadingR ? (
                  <Alert severity="warning">
                    {t('Data not available for this zone.')}
                  </Alert>
                ) : (
                  ifapaSubheader
                )
              }
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent style={{ height: '100%' }}>
              <ReactEChartsCore
                echarts={echarts}
                style={{ height: '90%' }}
                showLoading={isLoadingR}
                option={{
                  color: '#004c96',
                  legend: {},
                  dataset: {
                    source: hasWeatherData ? formatDataI() : [],
                  },
                  xAxis: { type: 'category' },
                  yAxis: {
                    name: t('IfapaData.Hum'),
                    type: 'value',
                    min: 0,
                    max: 100,
                    axisLabel: { formatter: '{value} %' },
                  },
                  series: [
                    {
                      type: 'line',
                      name: t('IfapaData.Hum'),
                      connectNulls: true,
                      encode: { x: 'day', y: 'hum' },
                      itemStyle: { color: '#AACFF3' },
                    },
                  ],

                  dataZoom: [
                    { type: 'inside' },
                    { start: 0, end: 20 },
                  ],
                  tooltip: {
                    trigger: 'axis',
                    formatter: (params) => {
                      if (!params?.length) return ''
                      const val = parseFloat(params[0].data[6])?.toFixed(2)
                      return `
                        ${params[0].value[0]}<br />
                        ${params[0].marker} ${t('IfapaData.Hum')} <b>${val} %</b>
                      `
                    },
                    axisPointer: {
                      type: 'cross',
                      animation: false,
                      label: { backgroundColor: '#505765' },
                    },
                  },
                }}
              />
            </CardContent>
          </Card>
        </div>
    </ResponsiveGridLayout>
    </div>
  )
}

export default AdvancedData
